import _ from 'lodash';
import React from 'react';
import {
    Box,
    Paper,
    Typography,
    Chip,
    Collapse,
    Divider
} from '@mui/material';
import {
    Person as PersonIcon,
    Business as BusinessIcon,
    ChevronRight as ChevronRightIcon,
    Description as DescriptionIcon
} from '@mui/icons-material';
import SourceButton from '../PhoneTab/SourceButton.js';
import { colors } from '../../styles/globals.js';
import {
    toTitleCase,
    BulletItem,
    DetailSectionLabel,
    LienJudgmentRecordCard,
    SectionHeader,
    EmptyState,
    transformLitigationRecord,
    deduplicateRecords,
    isValidRecord,
    isLienOrJudgment,
    caseReferencesCompany
} from '../LitigationsTab/LitigationsTab.js';
import {
    RemoveUCCLienButton,
    RemoveLitigationRecordButton,
} from '../EditMode/CompanyDetailEditControls';

// Card for UCC Liens (file_number, type, debtors, secured_parties, filing_date, lapse_date)
const UCCLienCard = ({
    fileNumber,
    type,
    debtors = [],
    securedParties = [],
    filingDate,
    lapseDate,
    status,
    source = [],
}) => {
    const isOpen = status === 'open';
    const [detailsExpanded, setDetailsExpanded] = React.useState(false);

    const detailItemCount = _.filter([type, lapseDate], Boolean).length
        + debtors.length + securedParties.length;

    const title = type ? toTitleCase(type) : 'UCC Lien';

    return (
        <Paper
            elevation={0}
            sx={{
                p: 2, borderRadius: 2, backgroundColor: '#F8FAFC',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)', width: 360, maxWidth: '100%',
                flexShrink: 0, display: 'flex', flexDirection: 'column',
                transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }
            }}
        >
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
                    <Box sx={{
                        backgroundColor: '#D97706', borderRadius: '6px', p: 0.5,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                    }}>
                        <DescriptionIcon sx={{ color: 'white', fontSize: 20 }} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="h6" sx={{
                            fontWeight: 600, color: '#1E293B', fontSize: '1rem', lineHeight: 1.3, wordBreak: 'break-word'
                        }}>
                            {title}
                        </Typography>
                        {fileNumber && (
                            <Typography sx={{ color: '#64748B', fontSize: '0.7rem', fontFamily: 'monospace', mt: 0.25 }}>
                                #{fileNumber}
                            </Typography>
                        )}
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5, flexShrink: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Chip
                            label={isOpen ? (filingDate ? `Filed ${new Date(filingDate).toLocaleDateString()}` : 'Open') : 'Closed'}
                            size="small"
                            sx={{
                                backgroundColor: isOpen ? '#FFFBEB' : '#DCFCE7',
                                color: isOpen ? '#D97706' : '#16A34A',
                                fontSize: '0.7rem', fontWeight: 600, height: '22px',
                            }}
                        />
                        {detailItemCount > 0 && (
                            <Box onClick={() => setDetailsExpanded(!detailsExpanded)} sx={{
                                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                borderRadius: '50%', width: 24, height: 24, '&:hover': { backgroundColor: '#FFFBEB' }
                            }}>
                                <ChevronRightIcon sx={{
                                    color: '#1E293B', fontSize: 20,
                                    transform: detailsExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                                    transition: 'transform 0.2s ease'
                                }} />
                            </Box>
                        )}
                    </Box>
                </Box>
            </Box>

            {/* Secured Party vs. Debtor subtitle */}
            {(securedParties.length > 0 || debtors.length > 0) && (
                <Typography sx={{
                    color: '#374151', fontSize: '0.875rem', fontWeight: 400, lineHeight: 1.3
                }}>
                    {securedParties.length > 0 && debtors.length > 0
                        ? `${toTitleCase(securedParties[0])} vs. ${toTitleCase(debtors[0])}`
                        : toTitleCase(securedParties[0] || debtors[0])}
                </Typography>
            )}

            {/* Details Section */}
            <Collapse in={detailsExpanded}>
                <Divider sx={{ my: 1.5 }} />
                <Box>
                    {type && <BulletItem label="Type" text={toTitleCase(type)} />}
                    {lapseDate && <BulletItem label="Lapse Date" text={new Date(lapseDate).toLocaleDateString()} />}

                    {debtors.length > 0 && (
                        <>
                            <DetailSectionLabel label="Debtors:" />
                            {debtors.map((d, i) => (
                                <BulletItem key={i} text={toTitleCase(d)} />
                            ))}
                        </>
                    )}

                    {securedParties.length > 0 && (
                        <>
                            <DetailSectionLabel label="Secured Parties:" />
                            {securedParties.map((p, i) => (
                                <BulletItem key={i} text={toTitleCase(p)} />
                            ))}
                        </>
                    )}
                </Box>
            </Collapse>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mt: 'auto', pt: 1.5 }}>
                <SourceButton sources={source} />
            </Box>
        </Paper>
    );
};

// Render the right card based on record type, with optional remove button in edit mode
const LienRecordCard = ({ record, onBadgeClick, liens, litigations, onUpdateLiens, onUpdateLitigations }) => {
    const card = record._isUCCLien
        ? <UCCLienCard {...record} />
        : <LienJudgmentRecordCard {...record} onBadgeClick={onBadgeClick} />;

    const isUCC = record._isUCCLien;
    const removeButton = isUCC
        ? (onUpdateLiens && (
            <RemoveUCCLienButton
                lienId={record.id}
                fileNumber={record.fileNumber}
                liens={liens}
                onUpdate={onUpdateLiens}
            />
        ))
        : (onUpdateLitigations && (
            <RemoveLitigationRecordButton
                caseNumber={record.caseNumber}
                originFilingNumber={record.originFilingNumber}
                recordId={record.id}
                recordType={record.recordType}
                originFilingType={record.originFilingType}
                litigations={litigations}
                onUpdate={onUpdateLitigations}
            />
        ));

    if (!removeButton) return card;

    return (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
            {card}
            {removeButton}
        </Box>
    );
};

// Sort: highest amount first, then most recent filing date, no amount/date at end
const sortByAmountThenDate = (a, b) => {
    const amountA = a.amount ? Number(a.amount) : null;
    const amountB = b.amount ? Number(b.amount) : null;

    // Both have amounts - sort by amount descending
    if (amountA !== null && amountB !== null) return amountB - amountA;
    // Only one has amount - it goes first
    if (amountA !== null && amountB === null) return -1;
    if (amountA === null && amountB !== null) return 1;

    // Neither has amount - sort by filing date descending
    const dateA = a.filingDateRaw || a.filingDate;
    const dateB = b.filingDateRaw || b.filingDate;
    if (!dateA && !dateB) return 0;
    if (!dateA) return 1;
    if (!dateB) return -1;
    return new Date(dateB) - new Date(dateA);
};

const transformLiensData = (litigations, liens, entity, ubos) => {
    // 1. Get lien_judgment records from litigations
    const lienJudgmentRaw = _.filter([
        ...(litigations?.open || []),
        ...(litigations?.closed || [])
    ], r => isValidRecord(r) && r.record_type === 'lien_judgment' && isLienOrJudgment(r));

    const lienJudgmentRecords = deduplicateRecords(lienJudgmentRaw);

    // 2. Get UCC liens
    const uccLiens = [
        ..._.map(liens?.open || [], l => ({ ...l, _status: 'open' })),
        ..._.map(liens?.closed || [], l => ({ ...l, _status: 'closed' }))
    ];

    const entityId = entity?.business_entity_id;
    const entityName = entity?.legal_name || '';
    const uboNodeIds = new Set(_.filter(_.map(ubos || [], ubo => ubo.node_id), Boolean));

    // --- Group lien_judgment records by entity/individual (same logic as litigations) ---
    const globalCaseMap = new Map();
    lienJudgmentRecords.forEach(record => {
        const cn = record.case_number;
        if (!cn) {
            globalCaseMap.set(`no_case_${Math.random()}`, { record, nodeIds: new Set([record.node_id || '']) });
            return;
        }
        if (globalCaseMap.has(cn)) {
            const entry = globalCaseMap.get(cn);
            entry.nodeIds.add(record.node_id || '');
            const existing = entry.record;
            if (record.parties_list?.length) {
                const existingNames = new Set(_.map(existing.parties_list || [], p => p.name));
                record.parties_list.forEach(p => {
                    if (!existingNames.has(p.name)) {
                        existing.parties_list = existing.parties_list || [];
                        existing.parties_list.push(p);
                    }
                });
            }
            if (!existing.amount && record.amount) existing.amount = record.amount;
            if (!existing.filing_date && record.filing_date) existing.filing_date = record.filing_date;
        } else {
            globalCaseMap.set(cn, { record: { ...record }, nodeIds: new Set([record.node_id || '']) });
        }
    });

    const entityRecords = [];
    const uboMap = new Map();
    (ubos || []).forEach(ubo => {
        uboMap.set(ubo.node_id, {
            name: ubo.name,
            role: ubo.role || 'Associated Person',
            records: []
        });
    });

    globalCaseMap.forEach(({ record, nodeIds }) => {
        const isEntityByNodeId = [...nodeIds].some(nid =>
            !nid || nid === entityId || nid === String(entityId) || !uboNodeIds.has(nid)
        );
        const belongsToEntity = isEntityByNodeId || caseReferencesCompany(record, entityName);
        const matchingUboIds = _.filter([...nodeIds], nid => uboMap.has(nid));

        const sections = [];
        if (belongsToEntity) sections.push({ type: 'entity', name: entityName });
        matchingUboIds.forEach(nid => {
            sections.push({ type: 'ubo', name: uboMap.get(nid).name });
        });

        if (sections.length === 0) {
            entityRecords.push(transformLitigationRecord(record));
            return;
        }

        if (belongsToEntity) {
            const transformed = transformLitigationRecord(record);
            if (matchingUboIds.length > 0) {
                transformed.alsoShownIn = matchingUboIds.map(nid => {
                    const name = uboMap.get(nid).name;
                    return { label: `Also shown under ${toTitleCase(name)}`, targetName: name };
                });
            }
            entityRecords.push(transformed);
        }

        matchingUboIds.forEach(nid => {
            const transformed = transformLitigationRecord(record);
            const otherSections = [];
            if (belongsToEntity) otherSections.push({ label: `Also shown under ${toTitleCase(entityName)}`, targetName: entityName });
            matchingUboIds.filter(id => id !== nid).forEach(id => {
                const name = uboMap.get(id).name;
                otherSections.push({ label: `Also shown under ${toTitleCase(name)}`, targetName: name });
            });
            if (otherSections.length > 0) transformed.alsoShownIn = otherSections;
            uboMap.get(nid).records.push(transformed);
        });
    });

    // --- Add UCC liens to entity records (they're entity-level, no node_id grouping) ---
    uccLiens.forEach(lien => {
        entityRecords.push({
            _isUCCLien: true,
            id: lien.id || null,
            fileNumber: lien.file_number,
            type: lien.type,
            debtors: lien.debtors || [],
            securedParties: lien.secured_parties || [],
            filingDate: lien.filing_date,
            filingDateRaw: lien.filing_date,
            lapseDate: lien.lapse_date,
            status: lien._status,
            amount: null,
            source: [{ source_url: "Secretary of State", fetched_at: new Date().toISOString() }],
        });
    });

    // Sort all sections
    entityRecords.sort(sortByAmountThenDate);
    const individuals = Array.from(uboMap.values());
    individuals.forEach(ind => ind.records.sort(sortByAmountThenDate));

    return {
        entity: {
            name: entity?.legal_name || 'Business Entity',
            records: entityRecords
        },
        individuals,
        sources: [{ source_url: "LexisNexis", fetched_at: new Date().toISOString() }]
    };
};

const LiensTab = ({ litigations, liens, entity, ubos, onUpdateLiens, onUpdateLitigations }) => {
    const data = React.useMemo(() => {
        const hasLitigationLiens = (litigations?.open || []).some(r => r.record_type === 'lien_judgment' && isLienOrJudgment(r))
            || (litigations?.closed || []).some(r => r.record_type === 'lien_judgment' && isLienOrJudgment(r));
        const hasUCCLiens = (liens?.open?.length > 0 || liens?.closed?.length > 0);

        if (hasLitigationLiens || hasUCCLiens) {
            return transformLiensData(litigations, liens, entity, ubos);
        }
        return null;
    }, [litigations, liens, entity, ubos]);

    const [expandedSections, setExpandedSections] = React.useState({});
    const sectionRefs = React.useRef({});

    const isSectionExpanded = (key) => expandedSections[key] !== false;
    const toggleSection = (key) => setExpandedSections(prev => ({ ...prev, [key]: !isSectionExpanded(key) }));

    const handleBadgeClick = React.useCallback((targetName) => {
        if (!data) return;
        const normalizedTarget = targetName.toLowerCase().trim();

        if (data.entity.name.toLowerCase().trim() === normalizedTarget) {
            setExpandedSections(prev => ({ ...prev, entity: true }));
            setTimeout(() => {
                sectionRefs.current['entity']?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 100);
            return;
        }

        const visibleIndividuals = _.filter(data.individuals, ind => {
                const name = (ind.name || '').toLowerCase().trim();
                return name && name !== 'unknown unknown' && name !== 'unknown' && ind.records.length > 0;
            })
            .sort((a, b) => b.records.length - a.records.length);

        const matchIdx = visibleIndividuals.findIndex(ind =>
            ind.name.toLowerCase().trim() === normalizedTarget
        );

        if (matchIdx !== -1) {
            const sectionKey = `individual-${matchIdx}`;
            setExpandedSections(prev => ({ ...prev, [sectionKey]: true }));
            setTimeout(() => {
                sectionRefs.current[sectionKey]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 100);
        }
    }, [data]);

    if (!data) {
        return (
            <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography sx={{ color: colors.grey }}>
                    No lien data available
                </Typography>
            </Box>
        );
    }

    return (
        <Box>
            {/* Entity Section */}
            <Box ref={el => sectionRefs.current['entity'] = el}>
                <SectionHeader
                    title={toTitleCase(data.entity.name)}
                    icon={BusinessIcon}
                    count={data.entity.records.length}
                    isEntity={true}
                    expanded={isSectionExpanded('entity')}
                    onToggle={() => toggleSection('entity')}
                />
            </Box>
            <Collapse in={isSectionExpanded('entity')}>
                {data.entity.records.length > 0 ? (
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 4, alignItems: 'flex-start' }}>
                        {_.map(data.entity.records, (record, index) => (
                            <LienRecordCard
                                key={index}
                                record={record}
                                onBadgeClick={handleBadgeClick}
                                liens={liens}
                                litigations={litigations}
                                onUpdateLiens={onUpdateLiens}
                                onUpdateLitigations={onUpdateLitigations}
                            />
                        ))}
                    </Box>
                ) : (
                    <Box sx={{ mb: 4 }}>
                        <EmptyState message="No records found" />
                    </Box>
                )}
            </Collapse>

            {/* Individual Sections */}
            {_.map(_.filter(data.individuals, ind => {
                    const name = (ind.name || '').toLowerCase().trim();
                    return name && name !== 'unknown unknown' && name !== 'unknown' && ind.records.length > 0;
                })
                .sort((a, b) => b.records.length - a.records.length), (individual, idx) => (
                <Box key={idx}>
                    <Box ref={el => sectionRefs.current[`individual-${idx}`] = el}>
                        <SectionHeader
                            title={`${toTitleCase(individual.name)} · ${toTitleCase(individual.role)}`}
                            icon={PersonIcon}
                            count={individual.records.length}
                            isEntity={false}
                            expanded={isSectionExpanded(`individual-${idx}`)}
                            onToggle={() => toggleSection(`individual-${idx}`)}
                        />
                    </Box>
                    <Collapse in={isSectionExpanded(`individual-${idx}`)}>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 4, alignItems: 'flex-start' }}>
                            {_.map(individual.records, (record, index) => (
                                <LienRecordCard
                                key={index}
                                record={record}
                                onBadgeClick={handleBadgeClick}
                                liens={liens}
                                litigations={litigations}
                                onUpdateLiens={onUpdateLiens}
                                onUpdateLitigations={onUpdateLitigations}
                            />
                            ))}
                        </Box>
                    </Collapse>
                </Box>
            ))}

            {/* Sources */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <SourceButton sources={data.sources || []} />
            </Box>
        </Box>
    );
};

export const getLiensDisplayCount = (litigations, liens, entity, ubos) => {
    const hasLitigationLiens = (litigations?.open || []).some(r => r.record_type === 'lien_judgment' && isLienOrJudgment(r))
        || (litigations?.closed || []).some(r => r.record_type === 'lien_judgment' && isLienOrJudgment(r));
    const hasUCCLiens = (liens?.open?.length > 0 || liens?.closed?.length > 0);

    if (!hasLitigationLiens && !hasUCCLiens) return 0;

    const data = transformLiensData(litigations, liens, entity, ubos);
    let count = data.entity.records.length;
    _.filter(data.individuals, ind => {
            const name = (ind.name || '').toLowerCase().trim();
            return name && name !== 'unknown unknown' && name !== 'unknown' && ind.records.length > 0;
        })
        .forEach(ind => { count += ind.records.length; });
    return count;
};

export default LiensTab;
